import { RouteWithGates } from '../api/routes';

export type RouteClusterGroup = {
  clusterId: number;
  clusterName: string;
  routes: RouteWithGates[];
};

/**
 * Group routes by geographic proximity based on their first
 * gate coordinates. Routes within ~220m lat / ~400m lon of
 * each other are placed in the same cluster. The cluster name
 * is the most frequent address among its routes.
 */
export function clusterRoutes(
  routes: RouteWithGates[],
): RouteClusterGroup[] {
  if (!routes.length) return [];

  const marginLat = 0.002;
  const marginLon = 0.005;
  let currentCoords: [number, number] | null = null;
  let cluster = 1;
  const tagged: { route: RouteWithGates; cluster: number }[] = [];

  const sorted = [...routes].sort((a, b) => {
    const aLat = a.origin_gates_lat?.[0]
      ?? a.gates?.[0]?.gates_lat?.[0] ?? 0;
    const bLat = b.origin_gates_lat?.[0]
      ?? b.gates?.[0]?.gates_lat?.[0] ?? 0;
    return aLat - bLat;
  });

  for (const route of sorted) {
    const lat = route.origin_gates_lat?.[0]
      ?? route.gates?.[0]?.gates_lat?.[0];
    const lon = route.origin_gates_lon?.[0]
      ?? route.gates?.[0]?.gates_lon?.[0];
    if (lat == null || lon == null) {
      tagged.push({ route, cluster });
      continue;
    }
    if (
      currentCoords === null
      || Math.abs(currentCoords[0] - lat) >= marginLat
      || Math.abs(currentCoords[1] - lon) >= marginLon
    ) {
      if (currentCoords !== null) cluster++;
      currentCoords = [lat, lon];
    }
    tagged.push({ route, cluster });
  }

  const groups: Record<number, RouteWithGates[]> = {};
  for (const { route: r, cluster: c } of tagged) {
    (groups[c] ??= []).push(r);
  }

  return Object.entries(groups)
    .map(([id, routesInCluster]) => {
      const addresses = routesInCluster
        .map((r) => r.address)
        .filter(Boolean);
      const modal = mostFrequent(addresses)
        || 'Unknown Location';
      return {
        clusterId: parseInt(id),
        clusterName: modal,
        routes: routesInCluster,
      };
    })
    .sort((a, b) => a.clusterId - b.clusterId);
}

function mostFrequent(arr: string[]): string {
  const counts: Record<string, number> = {};
  let best = '';
  let max = 0;
  for (const v of arr) {
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > max) {
      max = counts[v];
      best = v;
    }
  }
  return best;
}
